/* eslint no-unsafe-optional-chaining: 0 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { useLazyQuery } from '@apollo/client';
import {
  MarkChatReadOutlined,
  MarkEmailReadOutlined,
  PetsOutlined, SendOutlined,
  TaskOutlined,
  ThumbsUpDownOutlined,
  VolunteerActivismOutlined,
} from '@mui/icons-material';
import { Grid, Skeleton } from '@mui/material';
import { intersection, round } from 'lodash';
import { GET_DASHBOARD_STATS, GET_PRE_SURRENDER_DASHBOARD_STATS } from '../../queries';
import {
  beforeCapture, getRate, hasEngagementData,
  DEFAULT_START_DAYS_AGO, DEFAULT_END_DAYS_AGO,
} from '../../utilities';
import DashboardStatCard from './DashboardStatCard';
import ErrorMessage from '../ErrorMessage';
import { useAwoContext } from '../AwoContextProvider';

function DashboardStats({
  triggerRefetch, setRefetch, careType, species,
}) {
  const { awo, loading: awoLoading } = useAwoContext();
  const isPreSurrender = awo?.preSurrenderDashboard;
  const showEngagementRate = hasEngagementData(awo?.dateCreated);

  let dashboardQuery = GET_DASHBOARD_STATS;
  if (isPreSurrender) {
    dashboardQuery = GET_PRE_SURRENDER_DASHBOARD_STATS;
  }

  const [getDashboardStats, {
    loading, error, data, refetch,
  }] = useLazyQuery(dashboardQuery, {
    skip: awoLoading,
    variables: {
      careType, species, startDaysAgo: DEFAULT_START_DAYS_AGO, endDaysAgo: DEFAULT_END_DAYS_AGO,
    },
    notifyOnNetworkStatusChange: true,
  });

  React.useEffect(() => {
    getDashboardStats();
  }, [getDashboardStats]);

  React.useEffect(() => {
    if (triggerRefetch) {
      refetch({ variables: { careType, species } });
      setRefetch(false);
    }
  }, [triggerRefetch, refetch, setRefetch, careType, species]);

  if (error) throw error;

  const activeCategories = [
    (!isPreSurrender && !data?.nps?.score) && 'general',
    isPreSurrender && 'pre-surrender',
    data?.nps?.score && 'nps',
  ];
  const checkVisibility = (categories) => !!intersection(categories, activeCategories).length;
  const noFoster = !awo.catFoster && !awo.dogFoster && !awo.kittenFoster;
  const noAdopters = !awo.dogAdopt && !awo.catAdopt;

  const {
    count: tasks = 0,
  } = data?.taskCount || {};
  const {
    adoptersEngaged: adopters = 0,
    fostersEngaged: fosters = 0,
    totalEngaged = 0,
  } = data?.currentMonthEngagementStats || {};
  const {
    adoptersEngaged: prevAdopters = 0,
    fostersEngaged: prevFosters = 0,
    totalEngaged: prevTotalEngaged = 0,
  } = data?.previousMonthEngagementStats || {};
  const {
    invitedCount: dogSupport = 0,
  } = data?.dogSupportStats || {};
  const {
    invitedCount: prevDogSupport = 0,
  } = data?.previousMonthDogSupportStats || {};
  const {
    invitedCount: catSupport = 0,
  } = data?.catSupportStats || {};
  const {
    invitedCount: prevCatSupport = 0,
  } = data?.previousMonthCatSupportStats || {};
  const {
    invitedCount: support = 0,
    optOutCount: optOut = 0,
  } = data?.supportStats || data?.totalSupportStats || {};
  const {
    invitedCount: prevSupport = 0,
    optOutCount: prevOptOut = 0,
  } = data?.previousMonthSupportStats || data?.totalPreviousMonthSupportStats || {};

  const cards = [
    {
      title: 'Open Tasks',
      text: loading ? <Skeleton /> : `${tasks === 0 ? 'No' : tasks.toLocaleString()} tasks`,
      subtextLabel: 'Pet parent feedback for review',
      icon: <TaskOutlined />,
      outline: true,
      tooltipTitle: 'Tasks are from the past 30 days and will be closed if not marked as done.',
      visible: true,
    },
    {
      title: 'Pet Parents Receiving Support',
      text: loading ? <Skeleton /> : `${support.toLocaleString()} total`,
      subtextNumber: loading
        ? undefined
        : getRate(prevSupport, support),
      subtextLabel: loading ? '' : 'from last period',
      icon: <VolunteerActivismOutlined />,
      tooltipTitle: 'Pet parents currently within the 30-day support period',
      visible: true,
    },
    {
      title: 'Dog Parents Receiving Support',
      text: loading ? <Skeleton /> : `${dogSupport.toLocaleString()} dogs`,
      subtextNumber: loading
        ? undefined
        : getRate(prevDogSupport, dogSupport),
      subtextLabel: loading ? '' : 'from last period',
      icon: <PetsOutlined />,
      tooltipTitle: 'Dog parents currently within the 30-day support period',
      visible: checkVisibility(['pre-surrender']),
    },
    {
      title: 'Cat Parents Receiving Support',
      text: loading ? <Skeleton /> : `${catSupport.toLocaleString()} cats`,
      subtextNumber: loading
        ? undefined
        : getRate(prevCatSupport, catSupport),
      subtextLabel: loading ? '' : 'from last period',
      icon: <PetsOutlined />,
      tooltipTitle: 'Cat parents currently within the 30-day support period',
      visible: checkVisibility(['pre-surrender']),
    },
    {
      title: 'Engagement Rate',
      text: loading ? <Skeleton /> : `${showEngagementRate ? round(totalEngaged * 100) : '-'}%`,
      subtextNumber: loading || !showEngagementRate
        ? undefined
        : getRate(
          round(prevTotalEngaged * 100),
          round(totalEngaged * 100),
        ),
      subtextLabel: loading || !showEngagementRate ? '' : 'from last period',
      icon: <SendOutlined />,
      tooltipTitle: 'Percentage of pet parents who are within the 30-day support period and have engaged with Petcademy by reviewing a lesson, messaging a trainer, or submitting a survey',
      visible: checkVisibility(['pre-surrender']),
    },
    {
      title: 'Engagement Rate: Adopters',
      // eslint-disable-next-line no-nested-ternary
      text: loading ? <Skeleton /> : (showEngagementRate ? `${round(adopters * 100)}%` : '-'),
      subtextNumber: loading || !showEngagementRate
        ? undefined
        : getRate(
          round(prevAdopters * 100),
          round(adopters * 100),
        ),
      subtextLabel: loading || !showEngagementRate ? '' : 'from last period',
      icon: <MarkEmailReadOutlined />,
      tooltipTitle: 'The percentage of adopters who have completed the program in the last 30 days and interacted with Petcademy by reviewing a lesson, messaging a trainer, or submitting a survey',
      visible: checkVisibility(['general', 'nps']) && !noAdopters,
    },
    {
      title: 'Engagement Rate: Fosters',
      // eslint-disable-next-line no-nested-ternary
      text: loading ? <Skeleton /> : (showEngagementRate ? `${round(fosters * 100)}%` : '-'),
      subtextNumber: loading || !showEngagementRate
        ? undefined
        : getRate(
          round(prevFosters * 100),
          round(fosters * 100),
        ),
      subtextLabel: loading || !showEngagementRate ? '' : 'from last period',
      icon: <MarkEmailReadOutlined />,
      tooltipTitle: 'The percentage of fosters who have completed the program in the last 30 days and interacted with Petcademy by reviewing a lesson, messaging a trainer, or submitting a survey',
      visible: checkVisibility(['general', 'nps']) && !noFoster,
    },
    {
      title: 'SMS Opt-In Rate',
      text: loading ? <Skeleton /> : `${Math.abs(getRate(support, optOut))}%`,
      subtextNumber: loading
        ? undefined
        : getRate(
          Math.abs(getRate(prevSupport, prevOptOut)),
          Math.abs(getRate(support, optOut)),
        ),
      subtextLabel: loading ? '' : 'from last period',
      icon: <MarkChatReadOutlined />,
      tooltipTitle: 'Percentage of pet parents opting-in for Petcademy support via SMS during the 30-day support period',
      visible: true,
    },
    {
      title: 'Net Promoter Score (NPS)',
      text: loading ? <Skeleton /> : `${data?.nps?.score} / 100 average`,
      subtextLabel: 'Measures pet parent satisfaction',
      icon: <ThumbsUpDownOutlined />,
      tooltipTitle: 'Measures pet parent satisfaction. A score above 70 is amazing!',
      visible: checkVisibility(['nps']),
    },
  ];

  const gridSystem = () => 4;

  return (
    <Grid container spacing={3}>
      {cards.map((c, idx) => c.visible
        && (
          <Grid key={c.title} item xs={12} md={gridSystem(idx)}>
            <DashboardStatCard
              title={c.title}
              text={c.text}
              subtextNumber={c.subtextNumber}
              subtextLabel={c.subtextLabel}
              href={c.href}
              icon={c.icon}
              outline={c.outline}
              tooltipTitle={c.tooltipTitle}
            />
          </Grid>
        ))}
    </Grid>
  );
}

export default Sentry.withErrorBoundary(DashboardStats, { fallback: <ErrorMessage />, beforeCapture });
